import { toast } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { createMemo, deleteMemo, listMemos, type Memo } from "@/api";
import { useListRefresh } from "@/components/refresh";
import { createLogger } from "@/logging";

const logger = createLogger("components/memo");

export type MemoLoadState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; data: readonly Memo[] }
  | { status: "error"; message: string };

export type MemoController = {
  state: MemoLoadState;
  // 이 표면에서 작성·삭제 mutation 을 허용하는지 정한다. false 면 작성창 대신 읽기 전용 안내를 그린다.
  canMutate: boolean;
  creating: boolean;
  reload: () => void;
  // 작성이 성공하면 true 다(작성창이 입력을 비우는 신호). 실패하거나 무시하면 false 다.
  onCreate: (content: string) => Promise<boolean>;
  onDelete: (memoUuid: string) => Promise<void>;
};

export type UseMemoControllerOptions = {
  // false 면 fetch 하지 않는다. 닫힌 팝업 같은 비활성 표면에서 불필요한 요청을 막는다(기본 true).
  enabled?: boolean;
  // false 면 GET 목록은 유지하고, 이 표면의 작성·삭제 mutation을 모두 막는다.
  canMutate?: boolean;
};

// 메모 목록 로드와 작성/삭제 상태를 한 곳에서 소유하는 controller 다. 각 표면(팝업/모달/스트립)이
// 한 번씩 호출해 같은 동작을 공유한다. 액션 실패는 toast 로 알린다.
export function useMemoController(
  datasetUuid: string,
  options: UseMemoControllerOptions = {},
): MemoController {
  const enabled = options.enabled ?? true;
  const canMutate = options.canMutate ?? true;
  // 작성/삭제가 끝나면 감싸는 목록을 다시 조회해 배지·미리보기를 맞춘다. 목록 밖(상세 Window)에서는 no-op 이다.
  const refreshList = useListRefresh();
  const [state, setState] = useState<MemoLoadState>({ status: "idle" });
  const [creating, setCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    void listMemos(datasetUuid)
      .then((resp) => {
        if (!cancelled) setState({ status: "success", data: resp.items });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("listMemos failed", error);
        setState({ status: "error", message: "메모를 불러오지 못했습니다." });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, datasetUuid, refreshKey]);

  async function handleCreate(content: string): Promise<boolean> {
    const trimmed = content.trim();
    if (trimmed === "" || !canMutate || creating) {
      return false;
    }
    setCreating(true);
    try {
      const created = await createMemo(datasetUuid, { content: trimmed });
      // 이미 로드돼 있으면 최신순 맨 앞에 추가해 즉시 반영한다. 아니면 다시 로드해 일관성을 맞춘다.
      if (state.status === "success") {
        setState({ status: "success", data: [created, ...state.data] });
      } else {
        setRefreshKey((key) => key + 1);
      }
      // 목록(카드/표)의 배지·최근 메모를 갱신한다.
      refreshList();
      setCreating(false);
      return true;
    } catch (error) {
      logger.error("createMemo failed", error);
      toast.error("메모 작성에 실패했습니다.");
      setCreating(false);
      return false;
    }
  }

  async function handleDelete(memoUuid: string): Promise<void> {
    if (!canMutate) return;
    try {
      await deleteMemo(datasetUuid, memoUuid);
      // 삭제는 목록이 로드돼 있을 때만 시작된다(패널/모달에 메모가 보임). 그래서 success 만 처리한다.
      if (state.status === "success") {
        const remaining = state.data.filter((memo) => memo.uuid !== memoUuid);
        setState({ status: "success", data: remaining });
        // 목록(카드/표)의 배지·최근 메모를 갱신한다.
        refreshList();
      }
    } catch (error) {
      logger.error("deleteMemo failed", error);
      toast.error("메모 삭제에 실패했습니다.");
    }
  }

  return {
    state,
    canMutate,
    creating,
    reload: () => setRefreshKey((key) => key + 1),
    onCreate: handleCreate,
    onDelete: handleDelete,
  };
}
